import type { CreateProductPayload, DeliveryType, PalletSize } from '../types';
import { canonDetails, canonPalletsFromRows, canonStocksFromState } from './canonical';
import type { CanonicalDetails, CanonicalPallet, CanonicalStock, ProductFormValues } from './types';

/** The form reduced to the three comparable shapes everything downstream uses. */
export const canonicalizeForm = (
  values: ProductFormValues
): {
  details: CanonicalDetails;
  stocks: CanonicalStock[];
  pallets: CanonicalPallet[];
} => ({
  details: canonDetails(
    values.name,
    values.sku,
    values.description,
    values.deliveryType,
    values.trackInventory,
    values.defaultLocationId || null
  ),
  stocks: canonStocksFromState(values.whState),
  pallets: canonPalletsFromRows(values.palletRows),
});

export const buildCreatePayload = (
  current: { details: CanonicalDetails; stocks: CanonicalStock[]; pallets: CanonicalPallet[] },
  imageFile: File | null
): CreateProductPayload => {
  const isPallet = current.details.deliveryType === 'PALLET';

  return {
    name: current.details.name,
    // SKU is optional on create; omit it when blank.
    sku: current.details.sku || undefined,
    // The route treats "" and absent differently; only send a real description.
    description: current.details.description || undefined,
    deliveryType: current.details.deliveryType as DeliveryType,
    trackInventory: current.details.trackInventory,
    stocks: current.stocks,
    palletSizes: (isPallet ? (current.pallets as unknown as PalletSize[]) : undefined) as
      | PalletSize[]
      | undefined,
    image: imageFile ?? null,
    defaultLocationId: current.details.defaultLocationId,
  };
};

/** The details PATCH body. Stocks and pallet sizes go to their own endpoints. */
export const buildDetailsPayload = (
  details: CanonicalDetails,
  imageFile: File | null,
  // Clear the saved photo. A new file wins, so this only matters when none is set.
  removeImage = false
) => ({
  name: details.name,
  // Keep "" on PATCH so an existing SKU can be explicitly cleared server-side.
  sku: details.sku,
  description: details.description,
  deliveryType: details.deliveryType as DeliveryType,
  // undefined, not null: an absent image must leave the saved one alone.
  image: imageFile || undefined,
  removeImage: removeImage || undefined,
  trackInventory: details.trackInventory,
  defaultLocationId: details.defaultLocationId,
});
